import { useState, type FormEvent } from 'react'
import { router } from '@inertiajs/react'
import type { AppShellProps } from '@/components/AppShell'
import RegistrationPageLayout from '@/components/RegistrationPageLayout'
import RegistrationStepIndicator from '@/components/RegistrationStepIndicator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { KeyRound, UserPlus } from 'lucide-react'
import type { EventTheme } from '@/lib/registrationFormShared'

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  currentLocale: string
  locales: string[]
  preselectedSession?: {
    id: number
    description: string
    time: string
    dayNumber?: number
  } | null
  wizard: {
    currentStep: number
    totalSteps: number
    hasAgendaStep: boolean
  }
  registrationLinkSlug?: string | null
  urls: AppShellProps['urls'] & {
    lookupToken: string
    newRegistration: string
  }
  translations: AppShellProps['translations'] & {
    welcome: string
    registrationKey: string
    enterRegistrationKey: string
    continue: string
    newRegistration: string
    alreadyRegistered: string
    register: string
    stepLanding: string
    stepForm: string
    stepAgenda: string
    stepConfirmation: string
    registeringForSession: string
    orLabel: string
  }
}

export default function RegistrationLanding(props: Props) {
  const {
    event,
    currentLocale,
    locales,
    preselectedSession,
    wizard,
    urls,
    registrationLinkSlug,
    translations: t,
    ...shell
  } = props
  const [token, setToken] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleLookup = (e: FormEvent) => {
    e.preventDefault()
    const normalized = token.replace(/\s+/g, '').trim()
    if (!normalized) return
    setSubmitting(true)
    router.post(urls.lookupToken, { token: normalized }, {
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <RegistrationPageLayout
      event={event}
      currentLocale={currentLocale}
      locales={locales}
      urls={urls}
      translations={t}
      pageTitle={`${t.welcome} – ${event.name}`}
      pageVariant="landing"
      registrationLinkSlug={registrationLinkSlug}
      {...shell}
    >
      <div className="ev-reg-card ev-reg-landing-card rounded-lg border p-6 sm:p-8">
        <RegistrationStepIndicator
          wizard={wizard}
          labels={{ stepLanding: t.stepLanding, stepForm: t.stepForm, stepAgenda: t.stepAgenda, stepConfirmation: t.stepConfirmation }}
        />

        <p className="ev-reg-welcome mb-8 text-center text-sm opacity-70">{t.welcome}</p>

        {preselectedSession && (
          <div className="ev-reg-session-banner mb-6 rounded-lg border px-4 py-3 text-center">
            <p className="ev-reg-session-banner-label text-xs uppercase tracking-wide opacity-60">{t.registeringForSession}</p>
            {preselectedSession.time && (
              <p className="ev-reg-session-banner-time mt-1 font-mono text-xs opacity-60">{preselectedSession.time}</p>
            )}
            <p className="ev-reg-session-banner-title mt-1 text-sm font-medium">{preselectedSession.description}</p>
          </div>
        )}

        <Button
          type="button"
          className="ev-reg-button ev-reg-new-registration-button w-full h-12 text-base font-medium"
          onClick={() => router.visit(urls.newRegistration)}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          {t.newRegistration}
        </Button>

        <div className="ev-reg-divider my-8 flex items-center gap-3">
          <div className="ev-reg-divider-line flex-1 border-t" />
          <span className="ev-reg-divider-label text-sm opacity-50">{t.orLabel}</span>
          <div className="ev-reg-divider-line flex-1 border-t" />
        </div>

        <form onSubmit={handleLookup} className="ev-reg-lookup-form space-y-4 rounded-lg border p-6">
          <p className="ev-reg-lookup-label text-sm font-medium">{t.alreadyRegistered}</p>
          <Input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={t.enterRegistrationKey}
            className="ev-reg-input ev-reg-lookup-input h-12 font-mono text-base"
            dir="ltr"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={submitting || !token.trim()}
            className="ev-reg-button ev-reg-button-secondary ev-reg-lookup-button w-full h-12 text-base font-medium"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            {t.continue}
          </Button>
        </form>
      </div>
    </RegistrationPageLayout>
  )
}
